import SvgHamburgerMenu from '@sats-group/icons/16/hamburger-menu';
import Button from '@sats-group/ui-lib/react/button';
import Text from '@sats-group/ui-lib/react/text';
import React, { useEffect, useRef } from 'react';
import { Logo } from '../logo/logo';
import { Menu } from '../menu/menu';
import { Messages } from '../messages/messages';
import type { Layout as Props } from './layout.types';

export const Layout: React.FC<React.PropsWithChildren<Props>> = ({
  actions,
  children,
  menu,
  menuTrigger,
  messages,
  title,
}) => {
  const popover = useRef<HTMLDivElement>(null);

  // NOTE: Open popovers are a pain to style, so we close them instead.
  const handleResize: ResizeObserverCallback = entries =>
    entries
      .map(entry => entry.target)
      .filter((target): target is HTMLElement => target instanceof HTMLElement)
      .filter(
        target =>
          window.getComputedStyle(target).getPropertyValue('content') ===
          '"closeme"'
      )
      .forEach(target => target.hidePopover());

  useEffect(() => {
    if (!popover.current) {
      return;
    }

    const observer = new ResizeObserver(handleResize);
    observer.observe(popover.current);

    return () => {
      if (popover.current) {
        observer.unobserve(popover.current);
      }
    };
  }, []);

  return (
    <div className="layout">
      <div className="layout__content">
        <div className="layout__secondary">
          <div
            className="layout__menu"
            id={menuTrigger.popoverTarget}
            popover="auto"
            ref={popover}
          >
            <Menu {...menu} />
          </div>
        </div>
        <main className="layout__main">
          <div className="layout__header">
            <Text
              elementName="h1"
              size={Text.sizes.headline2}
              theme={Text.themes.emphasis}
              tight
            >
              {title}
            </Text>
            {actions ? <div>{actions}</div> : null}
          </div>
          <div className="layout__children">{children}</div>
        </main>
      </div>
      <aside className="layout__bar">
        <Button
          {...menuTrigger}
          leadingIcon={<SvgHamburgerMenu />}
          popoverTargetAction="toggle"
          size={Button.sizes.small}
          variant={Button.variants.secondary}
        />
        <div role="img" title="SATS Corporate">
          <Logo elementName="div" inline small variant="dark" />
        </div>
      </aside>
      <Messages {...messages} />
    </div>
  );
};
